/**
 * Expert Report API, v2.1
 * Volá /api/ai-report pro generování expertního výkladového reportu (ROZŠÍŘENÁ / PRO).
 * Přidán timeout (120s), retry (1×), a lepší error messages.
 */
import { buildValuationRequest } from './valuationCalculator';
import { authHeaders } from '../lib/apiFetch';

const API_BASE = process.env.REACT_APP_API_BASE || '';
const TIMEOUT_MS = 300_000; // 300 sekund (5 minut), Claude API může trvat i déle při komplexních reportech

async function fetchWithTimeout(url, options, timeoutMs = TIMEOUT_MS) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
    });
    return response;
  } finally {
    clearTimeout(timer);
  }
}

export async function generateAIReport(wizardState, tier) {
  const body = {
    valuation_request: buildValuationRequest(wizardState),
    tier,
    // Server pustí placený report jen k zaplacené uložené valuaci.
    valuation_id: wizardState.savedValuationId || null,
  };

  const maxAttempts = 2;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetchWithTimeout(`${API_BASE}/api/ai-report`, {
        method: 'POST',
        headers: await authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || `Server vrátil chybu ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      const isLastAttempt = attempt === maxAttempts;

      // Timeout
      if (error.name === 'AbortError') {
        if (isLastAttempt) {
          return { success: false, error: 'Generování reportu trvá příliš dlouho. Zkuste to prosím znovu.' };
        }
        continue;
      }

      // Network error (Failed to fetch), retry once
      if (error.message === 'Failed to fetch' || error.name === 'TypeError') {
        if (isLastAttempt) {
          return {
            success: false,
            error: 'Nelze se připojit k serveru. Zkontrolujte, že backend běží na ' + API_BASE,
          };
        }
        // Wait 2s before retry
        await new Promise(r => setTimeout(r, 2000));
        continue;
      }

      // Server error (already parsed detail)
      console.error('Expert Report API error:', error);
      return { success: false, error: error.message };
    }
  }
}
